import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './CaseStudies.css';

const CaseStudies = () => {

  const caseStudies = [
    {
      tag: 'FINANCIAL SERVICES',
      title: 'AI-Powered Credit Risk Assessment',
      challenge: 'A leading UK lender struggled with slow, manual credit review processes that created bottlenecks and inconsistent risk evaluations across their portfolio.',
      solution: 'Deployed aivionScore — an automated credit analysis platform using ML-driven risk scoring, document extraction, and real-time portfolio dashboards.',
      metrics: [
        { value: '65%', label: 'Faster credit decisions' },
        { value: '40%', label: 'Reduction in manual review' },
        { value: '22%', label: 'Improved risk accuracy' }
      ],
      link: '/credit-risk'
    },
    {
      tag: 'EDUCATION',
      title: 'AI-assisted Exam Assessment at Scale',
      challenge: 'A university needed to process thousands of handwritten exam papers each term, facing inconsistent marking and delayed student feedback.',
      solution: 'Implemented aivionGrade — AI-powered marking with computer vision, rubric-based evaluation, and detailed analytics dashboards for faculty.',
      metrics: [
        { value: '80%', label: 'Reduction in marking time' },
        { value: '95%', label: 'Marking consistency rate' },
        { value: '3x', label: 'Faster feedback to students' }
      ],
      link:"/exam-assessment"
    },
    {
      tag: 'HEALTHCARE',
      title: 'Blood Bank Management Solution',
      challenge: 'A blood bank in India needed to enhance its operations to be fully compliant with regulatory guidelines.',
      solution: 'Developed a comprehensive blood bank management system designed to enforce compliance and traceability.',
      metrics: [
        { value: '100%', label: 'Regulatory compliance' },
        { value: '50%', label: 'Increase in operational efficiency' },
        { value: '₹2M', label: 'Annual efficiency savings' }
      ],
      link: '/blood-bank-management'
    },
    {
      tag: 'ENERGY & UTILITIES',
      title: 'Automated Fact Checking for Reports',
      challenge: 'A company needed to ensure the accuracy of claims and facts in annual, quarterly, and ESG reports.',
      solution: 'Built an automated fact-checking system that cross-referenced claims against internal sources.',
      metrics: [
        { value: '95%', label: 'Accuracy rate of published reports' },
        { value: '70%', label: 'Faster report verification' },
        { value: '£1M', label: 'Annual cost savings' }
      ],
      link: '/fact-checking-reports'
    },
    {
      tag: 'FINANCIAL SERVICES',
      title: 'Reducing False Positives in Financial Crime Monitoring',
      challenge: "A challenger bank's transaction monitoring system was generating tens of thousands of low-quality alerts per month, overwhelming the financial crime team.",
      solution: 'Deployed an ML-based alert triage layer that scores legacy alerts and surfaces only the highest-risk cases to investigators.',
      metrics: [
        { value: '70%', label: 'Fewer alerts to review' },
        { value: '2.5x', label: 'More true positives caught' },
        { value: '£1.4M', label: 'Annual savings' }
      ],
      link: '/financial-crime-monitoring'
    },
    {
      tag: 'EDUCATION',
      title: 'GenAI Tutor for an Online Learning Platform',
      challenge: "An edtech provider wanted to offer 24/7 tutoring without exploding their cost-to-serve, while maintaining academic integrity.",
      solution: "Built a retrieval-augmented GenAI tutor grounded in the provider's curriculum, with strict guardrails and human-in-the-loop escalation.",
      metrics: [
        { value: '60%', label: 'Reduction in support cost' },
        { value: '92%', label: 'Learner satisfaction' },
        { value: '24/7', label: 'Tutor availability' }
      ],
      link: '/genai-tutor'
    },
    {
      tag: 'HEALTHCARE',
      title: 'Elective Recovery and Theatre Optimisation',
      challenge: 'An NHS trust faced one of the longest elective waiting lists in its region with limited theatre capacity to address it.',
      solution: 'Deployed demand & capacity forecasting and theatre-scheduling optimisation across surgical specialties.',
      metrics: [
        { value: '18%', label: 'More cases per list' },
        { value: '12wks', label: 'Waiting list reduction' },
        { value: '£3M', label: 'Income recovered' }
      ],
      link: '/theatre-optimisation'
    },
    {
      tag: 'ENERGY & UTILITIES',
      title: 'Short-Term Generation Forecasting for Renewables Trader',
      challenge: 'A renewables trading desk was being penalised by imbalance charges due to inaccurate short-term wind generation forecasts.',
      solution: 'Built a probabilistic short-term forecasting platform combining numerical weather prediction with site-level ML.',
      metrics: [
        { value: '28%', label: 'Lower forecast error' },
        { value: '£4.5M', label: 'Annual imbalance saved' },
        { value: '15min', label: 'Refresh cadence' }
      ],
      link: '/generation-forecasting'
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const nextSlide = () => {
    setCurrentIndex(
      (prev) => (prev + 1) % caseStudies.length
    );
  };

  const prevSlide = () => {
    setCurrentIndex(
      (prev) =>
        prev === 0
          ? caseStudies.length - 1
          : prev - 1
    );
  };

  const visibleCards = [];
  const cardsToShow = isMobile ? 1 : 3;

  for (let i = 0; i < cardsToShow; i++) {
    visibleCards.push(
      caseStudies[
        (currentIndex + i) %
        caseStudies.length
      ]
    );
  }

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');

    if (contactSection) {

      const navHeight =
        document.querySelector('.navbar')
          ?.offsetHeight || 0;

      const offsetTop =
        contactSection.offsetTop -
        navHeight -
        20;

      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section
      id="case-studies"
      className="section"
    >
      <div className="container">

        <div className="section-header">
          <h2>Case Studies</h2>
          <p>
            Real-world examples of how AIVION
            delivers measurable outcomes across
            regulated industries.
          </p>
        </div>

        <div className="carousel-wrapper">

          <button
            className="carousel-btn left"
            onClick={prevSlide}
          >
            ←
          </button>

          <div className="case-studies-grid">

            {visibleCards.map((study, index) => (

              <Link
                key={index}
                to={study.link}
                className="case-study-card"
              >

                <span className="case-tag">
                  {study.tag}
                </span>

                <h3>{study.title}</h3>

                <div className="case-block">
                  <h4>CHALLENGE</h4>
                  <p>{study.challenge}</p>
                </div>

                <div className="case-block">
                  <h4>SOLUTION</h4>
                  <p>{study.solution}</p>
                </div>

                <div className="case-divider"></div>

                <div className="case-metrics">

                  {study.metrics.map(
                    (metric, mIndex) => (
                      <div
                        key={mIndex}
                        className="metric"
                      >
                        <h5>{metric.value}</h5>
                        <span>{metric.label}</span>
                      </div>
                    )
                  )}

                </div>

              </Link>

            ))}

          </div>

          <button
            className="carousel-btn right"
            onClick={nextSlide}
          >
            →
          </button>

        </div>

        <div className="case-studies-cta">

          <h3>
            Ready to Be Our Next Success Story?
          </h3>

          <p>
            Let's discuss how AIVION can
            deliver measurable outcomes
            for your organisation.
          </p>

          <button
            className="case-cta-btn"
            onClick={scrollToContact}
          >
            GET IN TOUCH
            <span>→</span>
          </button>

        </div>

      </div>
    </section>
  );
};

export default CaseStudies;